import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';

export interface ConfirmationDialogData {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonColor?: 'primary' | 'warn' | 'accent';
  showCancelButton?: boolean;
  showConfirmButton?: boolean;
  showOkButton?: boolean;
}

@Component({
  selector: 'lib-confirmation-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule],
  template: `
    <mat-dialog-content>
      <div class="p-8">
        <h2 class="text-xl font-semibold text-center mb-4" *ngIf="data.title">
          {{ data.title }}
        </h2>
        <div class="text-left text-tb-letter whitespace-pre-line max-h-96 overflow-y-auto" 
             [ngClass]="{'error-message': isErrorMessage(), 'error-text': isErrorMessage()}">
          {{ data.message }}
        </div>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions>
      <!-- OK Button Layout (for error messages) -->
      <div class="px-8 py-2 w-full flex justify-end" *ngIf="shouldShowOkButton()">
        <button
          class="px-8 h-12 border-2 border-solid font-bold rounded-md text-white border-mdPrimary-500 bg-mdPrimary-500"
          (click)="onOk()"
        >
          OK
        </button>
      </div>
      
      <!-- Cancel/Confirm Button Layout (for existing functionality) -->
      <div class="px-8 py-2 w-full flex justify-around" *ngIf="!shouldShowOkButton()">
        <button
          class="px-8 h-12 bg-transparent rounded-md text-[#4B4B4B] font-bold border-2 border-solid border-[#4B4B4B]"
          (click)="onCancel()"
          *ngIf="shouldShowCancelButton()"
        >
          {{ data.cancelText || 'Cancel' }}
        </button>
        <button
          class="px-8 h-12 border-2 border-solid font-bold rounded-md text-white"
          [ngClass]="getConfirmButtonClass()"
          (click)="onConfirm()"
          *ngIf="shouldShowConfirmButton()"
        >
          {{ data.confirmText || 'Confirm' }}
        </button>
      </div>
    </mat-dialog-actions>
  `,
  styles: [
    `
      ::ng-deep {
        .mat-mdc-dialog-surface {
          border-radius: 15px !important;
          background-color: #fafafa !important;
        }
        .mat-mdc-dialog-container-with-actions .mat-mdc-dialog-content {
          overflow-x: hidden !important;
          padding: unset !important;
        }
        .error-message {
          background-color: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 12px;
          margin: 8px 0;
        }
        .error-text {
          color: #dc2626 !important;
          font-weight: 500;
        }
        .error-message .error-number {
          font-weight: bold;
          color: #dc2626;
        }
      }
    `,
  ],
})
export class ConfirmationDialogComponent {
  constructor(
    private dialogRef: MatDialogRef<ConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmationDialogData
  ) { }

  onConfirm() {
    this.dialogRef.close('confirm');
  }

  onCancel() {
    this.dialogRef.close('cancel');
  }

  onOk() {
    this.dialogRef.close('ok');
  }

  getConfirmButtonClass(): string {
    switch (this.data.confirmButtonColor) {
      case 'warn':
        return 'border-red-600 bg-red-600';
      case 'accent':
        return 'border-blue-600 bg-blue-600';
      case 'primary':
      default:
        return 'border-mdPrimary-500 bg-mdPrimary-500';
    }
  }

  isErrorMessage(): boolean {
    return this.data.title?.toLowerCase().includes('error') ||
      this.data.message.toLowerCase().includes('error');
  }

  shouldShowOkButton(): boolean {
    // Show OK button if explicitly requested or if it's an error message
    return this.data.showOkButton === true ||
      (this.isErrorMessage() && this.data.showOkButton !== false);
  }

  shouldShowCancelButton(): boolean {
    // Show cancel button if not showing OK button and not explicitly hidden
    return !this.shouldShowOkButton() && this.data.showCancelButton !== false;
  }

  shouldShowConfirmButton(): boolean {
    // Show confirm button if not showing OK button and not explicitly hidden
    return !this.shouldShowOkButton() && this.data.showConfirmButton !== false;
  }
} 